import { Alert, Descriptions, Form, Input, Modal, Select, Tag, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  SETTLEMENT_MODES,
  updateAgent,
  type AgentNode,
  type SettlementMode,
} from './agentApi'

interface AgentEditModalProps {
  open: boolean
  node: AgentNode
  onClose: () => void
}

interface EditValues {
  code: string
  displayName: string
  settlementMode: SettlementMode
}

/**
 * Agent/resellerni tahrirlash.
 *
 * <h2>⚠️ Nima TAHRIRLANADI va nima yo'q</h2>
 * <ul>
 *   <li><b>Kod, nom, hisob-kitob usuli</b> — shu yerda.</li>
 *   <li><b>Turi</b> (`kind`) — TAHRIRLANMAYDI, sabab
 *       {@link updateAgent} kirish tipida yozilgan: `RESELLER → AGENT`
 *       narx ro'yxatini egasiz qoldiradi (ADR-034 dagi bir xil xavf),
 *       teskarisi esa huquq to'plamini kengaytiradi. Maydon o'rniga
 *       joriy tur KO'RSATILADI va nega o'zgarmasligi aytiladi —
 *       yashirish «bu maydon yo'q ekan» degan savolni javobsiz
 *       qoldirardi.</li>
 *   <li><b>Holat</b> — `AgentGovernance` kartasida
 *       (`POST /{id}/state`). Ikkinchi boshqaruv ikkilanish paydo
 *       qilardi, ustiga bu yerdagi forma `CLOSED` ni qaytara
 *       olmaydi — u terminal holat.</li>
 *   <li><b>Ota tugun</b> — «Tuzilmada ko'chirish» (`AgentMoveModal`):
 *       u butun shoxni olib ketadi va tasodifan bo'lmasligi kerak.</li>
 * </ul>
 *
 * <h2>⚠️ HOLAT SO'ROVDA YUBORILADI, garchi formada bo'lmasa ham</h2>
 * Server `state` ni SHARTSIZ yozadi va bo'sh qiymatni `ACTIVE` ga
 * aylantiradi (`AgentDraft.stateOrDefault`). Uni yubormaslik
 * to'xtatilgan yoki yopilgan agentni jimgina TIRILTIRARDI — panel esa
 * «nomini o'zgartirdim» deb turardi.
 */
export function AgentEditModal({ open, node, onClose }: AgentEditModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<EditValues>()

  const save = useMutation({
    mutationFn: (values: EditValues) =>
      updateAgent(node.id, {
        code: values.code.trim(),
        displayName: values.displayName.trim(),
        settlementMode: values.settlementMode,
        // ⚠️ Formadan EMAS, tugunning joriy holatidan — klass izohiga qarang
        state: node.state,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.all })
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('agents.edit.title', { name: node.displayName })}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      {/* «Kod band» (409) SHU YERDA qoladi — aynan kodni tuzatish uchun */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      {/*
        ⚠️ O'ZGARMAYDIGAN MAYDONLAR KO'RSATILADI, YASHIRILMAYDI: operator
        «turini ham shu yerdan almashtiraman» deb kutadi va bo'sh joy
        unga hech narsa aytmasdi.
      */}
      <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('agents.kind')}>
          {node.kind === null ? (
            <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
          ) : (
            <Tag color={node.kind === 'RESELLER' ? 'purple' : 'blue'}>
              {t(`agentKind.${node.kind}`)}
            </Tag>
          )}
          <Typography.Text type="secondary" style={{ fontSize: 12, marginInlineStart: 8 }}>
            {t('agents.edit.kindLocked')}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={t('subscribers.columns.status')}>
          <Tag color={node.state === 'ACTIVE' ? 'green' : 'default'}>
            {t(`agentState.${node.state}`)}
          </Tag>
          <Typography.Text type="secondary" style={{ fontSize: 12, marginInlineStart: 8 }}>
            {t('agents.edit.stateElsewhere')}
          </Typography.Text>
        </Descriptions.Item>
      </Descriptions>

      {/*
        `destroyOnHidden` + `preserve={false}` bilan forma har ochilishda
        qaytadan quriladi, ya'ni `initialValues` har safar TANLANGAN
        tugundan o'qiladi — oldingi agentning qiymati yopishib qolmaydi.

        ⚠️ `settlementMode` `null` bo'lishi mumkin (turi bu servis
        modelidan tashqarida). Server bo'sh qiymatni `DEPOSIT` ga
        aylantiradi, shuning uchun tanlov OSHKORA `DEPOSIT` dan
        boshlanadi: jimgina yozilgan qiymatdan ko'ra ko'rinib turgani
        yaxshi.
      */}
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={save.isPending}
        initialValues={{
          code: node.code,
          displayName: node.displayName,
          settlementMode: node.settlementMode ?? 'DEPOSIT',
        }}
      >
        <Form.Item
          name="displayName"
          label={t('agents.displayName')}
          rules={[{ required: true, message: t('agents.displayNameRequired') }]}
        >
          <Input autoFocus autoComplete="off" maxLength={200} />
        </Form.Item>

        {/* ⚠️ Naqsh serverdagi `@Pattern` ning aynan o'zi — aks holda
            operator sababsiz 400 olardi */}
        <Form.Item
          name="code"
          label={t('agents.code')}
          extra={t('agents.edit.codeHint')}
          rules={[
            { required: true, message: t('agents.codeRequired') },
            { pattern: /^[a-zA-Z0-9._-]{2,64}$/, message: t('agents.codeFormat') },
          ]}
        >
          <Input autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="settlementMode"
          label={t('agents.settlementMode')}
          extra={t('agents.settlementHint')}
        >
          <Select
            options={SETTLEMENT_MODES.map((m) => ({ value: m, label: t(`settlementMode.${m}`) }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
